import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PlusCircle, LogOut, FileText, User, Calendar, TrendingUp, Target, UserPlus, Upload, Newspaper, KeyRound, ShieldCheck } from 'lucide-react';
import {
  getCurrentUser,
  logout,
  getAllProposals,
  deleteProposal,
  getProposalStats,
  getLegacyDataCounts,
  importLegacyData
} from '../utils/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legacyCounts, setLegacyCounts] = useState(getLegacyDataCounts());
  const [importing, setImporting] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    sent: 0,
    accepted: 0
  });

  useEffect(() => {
    document.title = 'Team Dashboard - Rubicon Finservices';
    fetchProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProposals = async () => {
    try {
      const [allProposals, statsData] = await Promise.all([getAllProposals(), getProposalStats()]);
      setProposals(allProposals);
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching proposals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleDelete = async (proposalId) => {
    if (!window.confirm('Are you sure you want to delete this proposal?')) {
      return;
    }

    try {
      await deleteProposal(proposalId);
      fetchProposals(); // Refresh list
    } catch (err) {
      console.error('Error deleting proposal:', err);
      alert('Failed to delete proposal: ' + err.message);
    }
  };

  const legacyTotal = legacyCounts.proposals + legacyCounts.risk_profiles + legacyCounts.onboardings;

  const handleImport = async () => {
    if (!window.confirm(`Import ${legacyCounts.proposals} proposal(s), ${legacyCounts.risk_profiles} risk profile(s) and ${legacyCounts.onboardings} onboarding record(s) from this browser into the shared database?`)) return;
    setImporting(true);
    try {
      const r = await importLegacyData();
      alert(`Import complete.\nProposals: ${r.proposals.imported} imported, ${r.proposals.skipped} skipped\nRisk profiles: ${r.risk_profiles.imported} imported, ${r.risk_profiles.skipped} skipped\nOnboardings: ${r.onboardings.imported} imported, ${r.onboardings.skipped} skipped`);
      setLegacyCounts(getLegacyDataCounts());
      fetchProposals();
    } catch (err) {
      alert('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Sent': 'bg-blue-100 text-blue-700',
      'Accepted': 'bg-green-100 text-green-700',
      'Rejected': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/7akuxub4_LOGO.png" 
                alt="Rubicon" 
                className="h-16"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900">Team Dashboard</h1>
                <p className="text-sm text-slate-600">Proposal Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600" data-testid="dashboard-welcome">
                Welcome, {getCurrentUser()?.username || 'User'}
                {getCurrentUser()?.role === 'admin' && <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-semibold">Admin</span>}
              </span>
              <Link to="/admin/users">
                <Button variant="outline" size="sm">
                  Manage Users
                </Button>
              </Link>
              <Link to="/admin/change-password">
                <Button variant="outline" size="sm" className="flex items-center gap-2" data-testid="change-password-link">
                  <KeyRound size={16} />
                  Change Password
                </Button>
              </Link>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut size={16} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {legacyTotal > 0 && (
          <div className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid="legacy-import-banner">
            <div>
              <p className="font-semibold text-amber-900">Older records found in this browser</p>
              <p className="text-sm text-amber-800">
                {legacyCounts.proposals} proposal(s), {legacyCounts.risk_profiles} risk profile(s), {legacyCounts.onboardings} onboarding record(s) are saved only on this computer. Import them once so your whole team can see them.
              </p>
            </div>
            <Button onClick={handleImport} disabled={importing} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="legacy-import-button">
              <Upload size={16} className="mr-2" />
              {importing ? 'Importing...' : 'Import from this browser'}
            </Button>
          </div>
        )}
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Proposals</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <FileText className="text-blue-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Draft</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.draft}</p>
                </div>
                <Calendar className="text-gray-600" size={40} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Link to="/admin/proposals/create">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <PlusCircle className="text-blue-600" size={32} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">Create Investment Proposal</h3>
                      <p className="text-sm text-slate-600">Generate personalized investment proposals for clients</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/admin/risk-profiling">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-green-500">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Target className="text-green-600" size={32} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">Investor Risk Profiling</h3>
                      <p className="text-sm text-slate-600">Assess investor risk tolerance and generate profile PDF</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/admin/onboarding">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-purple-500">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-purple-100 p-3 rounded-lg">
                      <UserPlus className="text-purple-600" size={32} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">Investor Onboarding</h3>
                      <p className="text-sm text-slate-600">Complete KYC and onboarding documentation</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {getCurrentUser()?.role === 'admin' && (
              <Link to="/admin/blog" data-testid="dashboard-blog-card">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-amber-500">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="bg-amber-100 p-3 rounded-lg">
                        <Newspaper className="text-amber-600" size={32} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-slate-900">Manage Blog</h3>
                        <p className="text-sm text-slate-600">Write, edit and publish blog articles</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            <Link to="/admin/data-requests" data-testid="dashboard-data-requests-card">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-rose-500">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-rose-100 p-3 rounded-lg">
                      <ShieldCheck className="text-rose-600" size={32} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">Data Rights Requests</h3>
                      <p className="text-sm text-slate-600">DPDP access, correction, erasure & grievances</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">All Proposals</h2>
          <Link to="/admin/proposals/create">
            <Button className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
              <PlusCircle size={20} />
              Create New Proposal
            </Button>
          </Link>
        </div>

        {/* Proposals List */}
        {proposals.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="mx-auto text-slate-300 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Proposals Yet</h3>
              <p className="text-slate-600 mb-6">Create your first client proposal to get started</p>
              <Link to="/admin/proposals/create">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Create Proposal
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {proposals.map((proposal) => (
              <Card key={proposal.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-slate-900">{proposal.client_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(proposal.status)}`}>
                          {proposal.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600">Email</p>
                          <p className="font-semibold text-slate-900">{proposal.client_email}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Phone</p>
                          <p className="font-semibold text-slate-900">{proposal.client_phone}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Investment</p>
                          <p className="font-semibold text-slate-900">{formatCurrency(proposal.total_investment)}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Risk Profile</p>
                          <p className="font-semibold text-slate-900">{proposal.risk_profile}</p>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-slate-600">
                        Created: {formatDate(proposal.created_at)} | Valid Until: {formatDate(proposal.valid_until)}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Link to={`/admin/proposals/${proposal.id}`}>
                        <Button variant="outline" size="sm">View</Button>
                      </Link>
                      <Link to={`/admin/proposals/${proposal.id}/edit`}>
                        <Button variant="outline" size="sm">Edit</Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(proposal.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
